/**
 * What the wall panel's Backsplash card reads and writes on a BATHROOM wall
 * (contractor call #9, step 3): the shower's tile, the vanity splash and the
 * tub backsplash. Pure.
 *
 * The vanity and tub bands are drawn from their fixture and redrawn when it
 * moves (`fixtureTextureRegionUtils`), so the card never edits a band's
 * rectangle: it edits the wall's setting — `wall.backsplashSpec.vanity` /
 * `.tub`, the rule `bathroomBandHeights` reads — and redraws the band from it
 * in the same write. The shower's height already has its rule, `tileHeight`
 * on the shower's regions, set by the cloud tiler; the card only reads it
 * here and sets it through `useShowerTileApply`.
 *
 * A band belongs to a FIXTURE — one vanity (its counter assembly,
 * `vanityExtentObjects`) or one tub — found by geometry, the way the
 * move/resize resync finds it, never by the band's `groupId`: Edit wall's
 * old Add Backsplash gives every vanity band in a room ONE group, so a group
 * spans vanities. A band on a wall is the fixture's whose drawn band it
 * overlaps along the wall. A band that wraps a corner is ONE band: a height,
 * a Remove and an Add each go on every wall it spans (`bandSpan`), and the
 * card names those walls. A kitchen's bands stay per wall
 * (`backsplashCard.js`).
 */
import { isGeneratedKitchenBacksplash } from '@shared-lib/kitchen-assembly/styling/kitchenBacksplash';
import { isShowerSurfaceRegion } from '@shared-lib/estimate-pricing/designCenter/surfaceRegions';
import {
  isShowerTileHeight,
  SHOWER_TILE_STANDARD_HEIGHT_IN,
} from '@shared-lib/geometry/showerWallTiling';
import {
  readBacksplashSpec,
  wallTopY,
} from '@shared-lib/kitchen-assembly/styling/backsplashSpec';
import { findShowerOnWall } from './showerWallHelpers';
import {
  isValidSystemChoice,
  systemChoiceLabel,
  systemChoiceOfTextureId,
  systemTextureId,
} from '@shared-lib/shower-systems';
import { roomHasTubShowerCombo } from '../constants/designCenterCategories';
import {
  computeTubBacksplashRegion,
  tubSurfaceTopY,
} from './bathtubSurroundHelpers';
import { isObjectType, isCountertop, isLowerCabinet } from './objectTypeUtils';
import { getParentWallIds } from './parentWallHelpers';
import {
  isBacksplashExtent,
  findWallsNearObjects,
  computeBacksplashRegionPure,
  mirrorOverVanitySplash,
  vanityExtentObjects,
  vanitySplashLines,
} from './vanitySplashGeometry';
import { tubWallIdsFor } from './fixtureTextureRegionUtils';
import { retileLinkedRegions } from './groupedRegionSync';
import { wallDisplayName } from './wallCardinal';
import { wallFaceSpanY, wallFaceHeight } from '@shared-lib/geometry/wallFace';
import { sameStoredValue } from '@shared-lib/rooms/sameStoredValue';

const SQ_M_PER_SQ_FT = 0.09290304;

/** The `surfaceType` each band is saved under. */
export const BAND_SURFACE = Object.freeze({
  vanity: 'backsplash',
  tub: 'tubBacksplash',
});

const IS_BAND = {
  shower: (r) => r?.surfaceType === 'showerWall' || isShowerSurfaceRegion(r),
  // a kitchen's generated band is the kitchen card's, never a vanity's
  vanity: (r) =>
    r?.surfaceType === 'backsplash' && !isGeneratedKitchenBacksplash(r),
  tub: (r) => r?.surfaceType === 'tubBacksplash',
};

/** A shower, vanity or tub band — what the bathroom card speaks for. */
export const isBathroomBandRegion = (r) =>
  IS_BAND.shower(r) || IS_BAND.vanity(r) || IS_BAND.tub(r);

/** Which of the card's bands a region is, or null. */
export const bathroomBandOf = (r) =>
  ['shower', 'vanity', 'tub'].find((band) => IS_BAND[band](r)) ?? null;

const regionsOf = (wall) => wall?.texture?.regions || [];
const bandsOn = (wall, band) => regionsOf(wall).filter(IS_BAND[band]);
const wallById = (room, id) => (room?.walls || []).find((w) => w.id === id);
const tileOf = (regions) =>
  regions.find((r) => r.type !== 'paint' && r.id)?.id ?? null;
// Bands and settings come back from Firestore with keys reordered; compared as
// text, an unchanged band read as changed (`sameStoredValue`).
const same = (a, b) => sameStoredValue(a ?? null, b ?? null);

// A band's percentages are of the DRAWN face (`wallFace`).
const faceHeightOf = (wall) => wallFaceHeight(wall) ?? 0;

const areaSqFt = (wall, regions) => {
  const len = wall?.dimensions?.x ?? 0;
  const h = faceHeightOf(wall);
  const m2 = regions.reduce(
    (sum, r) => sum + (r.width / 100) * len * ((r.height / 100) * h),
    0
  );
  return m2 / SQ_M_PER_SQ_FT;
};

/**
 * Every fixture of a band kind in the room, with the walls its band stands
 * on and how to draw it on one of them — each vanity's counter assembly
 * (the objects the resync sizes it by) or each tub.
 *
 * @returns {Array<{walls: string[], draw: (room, wallId) => ?rect,
 *   extent?: Object[]}>}
 */
function fixturesOf(room, band) {
  const objects = room?.objects || [];
  if (band === 'tub') {
    return objects
      .filter((o) => isObjectType(o, 'bathtub'))
      .map((tub) => {
        const walls = tubWallIdsFor(tub, room);
        return {
          tub,
          walls,
          draw: (r, wallId) =>
            walls.includes(wallId)
              ? computeTubBacksplashRegion(tub, wallId, r, walls)
              : null,
        };
      });
  }
  const seen = new Set();
  const out = [];
  for (const obj of objects) {
    if (seen.has(obj.id) || !isBacksplashExtent(obj)) continue;
    const extent = vanityExtentObjects(obj, room);
    extent.forEach((o) => seen.add(o.id));
    if (!extent.some((o) => isCountertop(o) || isLowerCabinet(o))) continue;
    const walls = new Set(findWallsNearObjects(extent, room));
    extent.forEach((o) => getParentWallIds(o).forEach((id) => walls.add(id)));
    out.push({
      walls: [...walls],
      extent,
      draw: (r, wallId) => computeBacksplashRegionPure(extent, wallId, r),
    });
  }
  return out;
}

const fixturesOn = (fixtures, wallId) =>
  fixtures.filter((f) => f.walls.includes(wallId));

const alongOverlap = (a, b) =>
  Math.min(a.x + a.width, b.x + b.width) - Math.max(a.x, b.x);

/**
 * Which of `fixtures` each band on `wall` belongs to: the one whose drawn
 * band it overlaps most along the wall. A Map of region → fixture; a band
 * no fixture's overlaps is no fixture's.
 */
function ownersOn(room, wall, band, fixtures) {
  const drawn = fixturesOn(fixtures, wall.id)
    .map((f) => ({ f, rect: f.draw(room, wall.id) }))
    .filter((d) => d.rect);
  const owners = new Map();
  for (const r of bandsOn(wall, band)) {
    let best = null;
    for (const d of drawn) {
      const overlap = alongOverlap(r, d.rect);
      if (overlap > 0 && (!best || overlap > best.overlap)) {
        best = { f: d.f, overlap };
      }
    }
    if (best) owners.set(r, best.f);
  }
  return owners;
}

/**
 * The walls the band of `fixtures` spans: this wall, and every wall carrying
 * a band one of them owns — in the room's wall order.
 */
function bandSpan(room, wallId, band, fixtures) {
  const reach = new Set(fixtures.flatMap((f) => f.walls));
  return room.walls
    .filter(
      (w) =>
        w.id === wallId ||
        (reach.has(w.id) && ownersOn(room, w, band, fixtures).size > 0)
    )
    .map((w) => w.id);
}

/** The fixtures' walls a Remove turned off, which an Add brings back. */
const offWallsOf = (room, wallId, band, fixtures) => {
  const reach = new Set(fixtures.flatMap((f) => f.walls));
  return room.walls
    .filter(
      (w) =>
        w.id !== wallId &&
        reach.has(w.id) &&
        w.backsplashSpec?.[band]?.off === true
    )
    .map((w) => w.id);
};

/**
 * Every wall of the room carrying shower tile. The shower's height is set for
 * the whole room (`apply_shower_tiling` takes a room), so a height chosen on
 * one of them reaches them all.
 */
export const showerTileWallIds = (room) =>
  (room?.walls || [])
    .filter((w) => bandsOn(w, 'shower').length > 0)
    .map((w) => w.id);

/**
 * The system a shower wall's PANEL comes with, or null for a tile: its name
 * ("Onyx Collection · Concrete · Gloss smooth") and the shower — or the tub
 * of a tub/shower — that carries the choice, so the card can open its System
 * section instead of offering the panel as a tile. The band's own fixture
 * group decides between two showers on one system; `showerId` is null when no
 * object carries that panel any more.
 */
function showerSystemOfBands(room, bands) {
  const choice = systemChoiceOfTextureId(tileOf(bands));
  if (!choice) return null;
  const panelId = systemTextureId(choice);
  const carriers = (room.objects || []).filter(
    (o) =>
      isValidSystemChoice(o?.showerSystem) &&
      systemTextureId(o.showerSystem) === panelId
  );
  const groups = new Set(bands.map((b) => b.fixtureGroupId).filter(Boolean));
  const carrier =
    carriers.find((o) => groups.has(o.fixtureGroupId)) ?? carriers[0] ?? null;
  return { showerId: carrier?.id ?? null, name: systemChoiceLabel(choice) };
}

/**
 * What the card shows for one wall of a bathroom, or null when the wall has
 * no shower, vanity or tub and no band of one.
 *
 * @returns {?{shower, vanity, tub}} each null when the wall has none
 */
export function wallBathroomBands(room, wallId) {
  const wall = wallById(room, wallId);
  if (!wall) return null;
  const spec = readBacksplashSpec(wall);

  const tubs = fixturesOn(fixturesOf(room, 'tub'), wallId);
  const vanities = fixturesOn(fixturesOf(room, 'vanity'), wallId);

  const showerBands = bandsOn(wall, 'shower');
  // A shower on this wall, or a tub/shower combo's tub — the tiler tiles a
  // combo's tub walls as its shower (`findShowerTilingTarget`).
  const showerHere =
    !!findShowerOnWall(wallId, room) ||
    (tubs.length > 0 && roomHasTubShowerCombo(room.objects));
  const shower =
    showerHere || showerBands.length
      ? {
          bands: showerBands,
          textureId: tileOf(showerBands),
          tileHeight: isShowerTileHeight(showerBands[0]?.tileHeight)
            ? showerBands[0].tileHeight
            : null,
          sqFt: areaSqFt(wall, showerBands),
          hasFixture: showerHere,
          system: showerSystemOfBands(room, showerBands),
          // The tile's height is the ROOM's (`apply_shower_tiling` takes a
          // room), so a height chosen here reaches every tiled wall.
          alsoWalls: showerTileWallIds(room)
            .filter((id) => id !== wallId)
            .map((id) => ({ id, name: wallDisplayName(room, id) })),
        }
      : null;

  const bandFacts = (band, fixtures, extra) => {
    const bands = bandsOn(wall, band);
    const setting = spec[band];
    if (!fixtures.length && !bands.length && !setting) return null;
    // The other walls a change here also moves — or, with the band off, the
    // ones + Add brings it back on.
    const also = bands.length
      ? bandSpan(room, wallId, band, fixtures).filter((id) => id !== wallId)
      : offWallsOf(room, wallId, band, fixtures);
    return {
      spec: setting && !setting.off ? setting : null,
      off: setting?.off === true,
      bands,
      textureId: tileOf(bands),
      sqFt: areaSqFt(wall, bands),
      hasFixture: fixtures.length > 0,
      alsoWalls: also.map((id) => ({ id, name: wallDisplayName(room, id) })),
      ...extra,
    };
  };

  const vanity = bandFacts('vanity', vanities, {
    hasMirror: vanities.some(
      (f) => mirrorOverVanitySplash(f.extent, wallId, room) != null
    ),
  });
  const tub = bandFacts('tub', tubs);

  return shower || vanity || tub ? { shower, vanity, tub } : null;
}

/** The wall with one band's setting replaced (`undefined` clears it). */
function withBandSpec(wall, band, value) {
  const spec = { ...(wall.backsplashSpec || {}) };
  if (value) spec[band] = value;
  else delete spec[band];
  const { backsplashSpec: _old, ...rest } = wall;
  return Object.keys(spec).length ? { ...rest, backsplashSpec: spec } : rest;
}

const withRegions = (wall, regions) => ({
  ...wall,
  texture: { ...(wall.texture || {}), regions },
});

/**
 * `room`'s walls with the bands of `fixtures` on `wallIds` redrawn by the
 * room's settings — each band from the fixture it belongs to, in its own
 * tile and markers.
 */
function redrawBands(room, wallIds, band, fixtures) {
  return room.walls.map((w) => {
    if (!wallIds.includes(w.id)) return w;
    const owners = ownersOn(room, w, band, fixtures);
    if (!owners.size) return w;
    return withRegions(
      w,
      regionsOf(w).map((r) => {
        const f = owners.get(r);
        const rect = f && f.draw(room, w.id);
        return rect ? { ...r, ...rect } : r;
      })
    );
  });
}

/** A band of these fixtures standing on another wall, and that wall. */
function bandElsewhere(room, wallId, band, fixtures) {
  for (const id of new Set(fixtures.flatMap((f) => f.walls))) {
    if (id === wallId) continue;
    const wall = wallById(room, id);
    const owners = ownersOn(room, wall, band, fixtures);
    const [found] = owners.keys();
    if (found) return { region: found, wall };
  }
  return null;
}

const newId = (prefix) =>
  `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;

/**
 * Put the band back — on `wallId` and on every wall of its fixtures a Remove
 * turned off, one band per fixture on each — in `textureId`, else the tile
 * the fixtures wear on another wall, else the tile it wore when removed; at
 * the height a wall still carrying it is set to.
 */
function addBand(room, { wallId, band, textureId }, ids) {
  const fixtures = fixturesOn(fixturesOf(room, band), wallId);
  if (!fixtures.length) return { walls: null, reason: 'no-fixture' };
  const linked = bandElsewhere(room, wallId, band, fixtures);
  const addTo = [wallId, ...offWallsOf(room, wallId, band, fixtures)];
  const removedIn = addTo
    .map((id) => wallById(room, id).backsplashSpec?.[band])
    .find((v) => v?.off === true && v.textureId)?.textureId;
  const tile = textureId ?? tileOf(linked ? [linked.region] : []) ?? removedIn;
  if (!tile) return { walls: null, reason: 'no-tile' };
  // the setting as stored on the sibling wall, not its normalised reading
  const sibling = linked?.wall.backsplashSpec?.[band];
  const setting = sibling && sibling.off !== true ? sibling : undefined;
  const walls = room.walls.map((w) =>
    addTo.includes(w.id) ? withBandSpec(w, band, setting) : w
  );
  const next = { ...room, walls };
  const regionId =
    ids.newRegionId ?? newId(band === 'tub' ? 'tubbacksplash' : 'backsplash');
  const groupId = linked?.region.groupId ?? ids.newGroupId ?? newId('group');
  let n = 0;
  const bandFor = (rect) => ({
    regionId: n++ ? `${regionId}_${n - 1}` : regionId,
    enabled: true,
    ...rect,
    surfaceType: BAND_SURFACE[band],
    groupId,
    ...(linked?.region.fixtureGroupId && {
      fixtureGroupId: linked.region.fixtureGroupId,
    }),
    ...(band === 'tub' && { source: 'bathtub' }),
    type: 'texture',
    id: tile,
  });
  const added = new Map(
    addTo.map((id) => [
      id,
      fixtures
        .filter((f) => f.walls.includes(id))
        .map((f) => f.draw(next, id))
        .filter(Boolean)
        .map(bandFor),
    ])
  );
  if (!added.get(wallId).length) return { walls: null, reason: 'no-fixture' };
  return {
    walls: walls.map((w) =>
      added.has(w.id)
        ? withRegions(
            w,
            regionsOf(w)
              .filter((r) => w.id !== wallId || !IS_BAND[band](r))
              .concat(added.get(w.id))
          )
        : w
    ),
    reason: null,
  };
}

/**
 * Take the band off every wall it spans: this wall's bands of the kind, and
 * the fixtures' bands on the others. A wall left with none is turned off —
 * remembering the tile, so + Add brings the band back as it was — and stays
 * off when the fixture moves; a wall still carrying another fixture's band
 * of the kind is left on.
 */
function removeBand(room, { wallId, band }) {
  const fixtures = fixturesOn(fixturesOf(room, band), wallId);
  const span = bandSpan(room, wallId, band, fixtures);
  const tile = tileOf(bandsOn(wallById(room, wallId), band));
  const off = { off: true, ...(tile && { textureId: tile }) };
  return {
    walls: room.walls.map((w) => {
      if (!span.includes(w.id)) return w;
      const owners = w.id === wallId ? null : ownersOn(room, w, band, fixtures);
      const kept = regionsOf(w).filter(
        (r) => !IS_BAND[band](r) || (owners && !owners.has(r))
      );
      const next = withRegions(w, kept);
      return kept.some(IS_BAND[band]) ? next : withBandSpec(next, band, off);
    }),
    reason: null,
  };
}

/**
 * The room's walls after a change the card makes to a vanity or tub band,
 * with the band redrawn in the same write — or why it cannot be made. Each
 * goes on every wall the band spans (`bandSpan`):
 *
 * - a height setting (`{ stop, inches }`);
 * - `{ off: true }` — removed, and kept off when the fixture moves;
 * - `{ off: false }` — put back, in `textureId`, else the tile the fixture
 *   wears on another wall, else the tile it wore when removed.
 *
 * @param {{newRegionId?, newGroupId?}} [ids] the ids a new band takes
 * @returns {{walls: ?Array, reason: ?string}} `reason` is null on success,
 *   else 'no-tile' (the host asks for one) | 'no-fixture'
 */
export function planBathroomBandChange(
  room,
  { wallId, band, change, textureId },
  ids = {}
) {
  if (change.off === true) return removeBand(room, { wallId, band });
  if (change.off === false) {
    return addBand(room, { wallId, band, textureId }, ids);
  }

  const fixtures = fixturesOn(fixturesOf(room, band), wallId);
  if (!fixtures.length) return { walls: null, reason: 'no-fixture' };
  const targets = bandSpan(room, wallId, band, fixtures);
  const next = {
    ...room,
    walls: room.walls.map((w) =>
      targets.includes(w.id) ? withBandSpec(w, band, change) : w
    ),
  };
  return { walls: redrawBands(next, targets, band, fixtures), reason: null };
}

/**
 * The room's walls with a tile picked for this wall's band — on every wall
 * of the fixtures on it — or, on a wall with no band, the band added in that
 * tile.
 */
export function planBathroomBandTile(
  room,
  { wallId, band, textureId },
  ids = {}
) {
  if (!bandsOn(wallById(room, wallId), band).length) {
    return addBand(room, { wallId, band, textureId }, ids);
  }
  const fixtures = fixturesOn(fixturesOf(room, band), wallId);
  const reach = new Set([wallId, ...fixtures.flatMap((f) => f.walls)]);
  return {
    walls: room.walls.map((w) => {
      if (!reach.has(w.id)) return w;
      const owners = ownersOn(room, w, band, fixtures);
      const { regions, changed } = retileLinkedRegions(
        regionsOf(w),
        (r) => IS_BAND[band](r) && (w.id === wallId || owners.has(r)),
        { textureId }
      );
      return changed.length ? withRegions(w, regions) : w;
    }),
    reason: null,
  };
}

/** `regions` with `bands` put where the band stood among them `before`. */
function placeBands(regions, bands, before, band) {
  const firstBand = before.findIndex(IS_BAND[band]);
  const at =
    firstBand < 0
      ? regions.length
      : before.slice(0, firstBand).filter((r) => !IS_BAND[band](r)).length;
  const i = Math.min(at, regions.length);
  return [...regions.slice(0, i), ...bands, ...regions.slice(i)];
}

/**
 * `room`'s walls with one band — its regions and its setting — put back on
 * every wall where it differs from `before`, everything else on the wall left
 * as it is now. The card's Undo takes back a band's SHAPE: the band is
 * redrawn from its fixture where the fixture stands now, at the setting put
 * back, and keeps a tile picked since. With `keepTile: false` — the Undo of
 * a tile pick — the band comes back exactly as it was, tile and all.
 */
export function restoreBathroomBand(
  room,
  before,
  band,
  { keepTile = true } = {}
) {
  const was = new Map((before || []).map((w) => [w.id, w]));
  const restored = [];
  const walls = (room.walls || []).map((w) => {
    const old = was.get(w.id);
    if (!old) return w;
    const oldSetting = old.backsplashSpec?.[band];
    if (
      same(bandsOn(w, band), bandsOn(old, band)) &&
      same(w.backsplashSpec?.[band], oldSetting)
    ) {
      return w;
    }
    restored.push(w.id);
    const nowTile = keepTile ? tileOf(bandsOn(w, band)) : null;
    let back = bandsOn(old, band);
    if (nowTile) {
      back = retileLinkedRegions(back, () => true, {
        textureId: nowTile,
      }).regions;
    }
    const kept = regionsOf(w).filter((r) => !IS_BAND[band](r));
    return withBandSpec(
      withRegions(w, placeBands(kept, back, regionsOf(old), band)),
      band,
      oldSetting
    );
  });
  if (!keepTile || !restored.length) return walls;
  const next = { ...room, walls };
  return redrawBands(next, restored, band, fixturesOf(next, band));
}

const INCH_M = 0.0254;
const wallBottomOf = (wall) =>
  wallFaceSpanY(wall)?.bottom ?? wall?.center?.y ?? 0;

/**
 * The lines a bathroom band's height is measured against on one wall, in
 * world Y — what its top edge snaps to when dragged on an elevation (call
 * item #28). `baseY` is what a custom height is measured up from: the
 * vanity's counter top, the tub's surface (even where a freestanding or
 * corner tub's band starts at the floor), the foot of the shower's tile.
 * A vanity or tub band is read against the fixture that owns the band
 * grabbed (`regionId`) — two vanities on one wall stand on two counters —
 * else the first on the wall.
 *
 * @returns {?{baseY, wallTopY, mirrorBottomY?}} null when the wall has no
 *   such band or fixture
 */
export function bathroomBandLines(room, wallId, band, regionId) {
  const wall = wallById(room, wallId);
  if (!wall) return null;
  if (band === 'shower') {
    const bands = bandsOn(wall, 'shower');
    if (!bands.length) return null;
    const h = faceHeightOf(wall);
    const footPct = Math.max(0, Math.min(...bands.map((r) => r.y ?? 0)));
    return {
      baseY: wallBottomOf(wall) + (footPct / 100) * h,
      wallTopY: wallTopY(wall),
    };
  }
  if (band !== 'vanity' && band !== 'tub') return null;
  const fixtures = fixturesOn(fixturesOf(room, band), wallId);
  if (!fixtures.length) return null;
  const grabbed = bandsOn(wall, band).find((r) => r.regionId === regionId);
  // A band no fixture owns is one the card cannot redraw either — measuring
  // it against another vanity's counter would move that vanity's band.
  const owner = grabbed
    ? ownersOn(room, wall, band, fixtures).get(grabbed)
    : fixtures[0];
  if (!owner) return null;
  if (band === 'tub') {
    return { baseY: tubSurfaceTopY(owner.tub, room), wallTopY: wallTopY(wall) };
  }
  const lines = vanitySplashLines(owner.extent, wallId, room);
  return (
    lines && {
      baseY: lines.counterTopY,
      mirrorBottomY: lines.mirrorBottomY,
      wallTopY: lines.wallTopY,
    }
  );
}

/**
 * The room's walls with every shower tile region re-cut to `height` —
 * 'standard', 84" up from its foot, or 'full', to the top — as the cloud
 * tiler (`apply_shower_tiling`, `tileSpanUpWall`) will cut it. What a shower
 * height dragged on an elevation draws until the tiler's own write lands.
 *
 * @param {object} room
 * @param {'standard'|'full'} height
 */
export function previewShowerTileHeight(room, height) {
  return (room?.walls || []).map((w) => {
    const h = faceHeightOf(w);
    if (!(h > 0) || !bandsOn(w, 'shower').length) return w;
    const standardPct = ((SHOWER_TILE_STANDARD_HEIGHT_IN * INCH_M) / h) * 100;
    return withRegions(
      w,
      regionsOf(w).map((r) => {
        if (!IS_BAND.shower(r)) return r;
        const y = Math.max(0, r.y ?? 0);
        const top = height === 'full' ? 100 : Math.min(100, y + standardPct);
        return { ...r, y, height: Math.max(0, top - y), tileHeight: height };
      })
    );
  });
}

/**
 * The room's shower tile height — one value when every tiled wall carries
 * the same `tileHeight`, else null (tiled before the rule, or mixed). The
 * height is the ROOM's (`apply_shower_tiling` takes a room), so this, not one
 * wall's, is what decides whether choosing a height changes anything.
 */
export function showerTileHeightOfRoom(room) {
  const heights = new Set(
    (room?.walls || []).flatMap((w) =>
      bandsOn(w, 'shower').map((r) =>
        isShowerTileHeight(r.tileHeight) ? r.tileHeight : null
      )
    )
  );
  return heights.size === 1 ? [...heights][0] : null;
}
